import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamation, faQuestion } from '@fortawesome/pro-solid-svg-icons';
import { faClock } from '@fortawesome/pro-regular-svg-icons';
import { type AppInfo } from '../../types';
import AppLogo from '../shared/AppLogo';
import AppLogoBadge, { type AppLogoBadgeKind } from '../shared/AppLogoBadge';

type Props = {
  /**
   * The 48px visual: the app's logo, or a grey circle with a question mark
   * (help answers), a clock (expired) or an exclamation mark (no app chosen yet).
   */
  visual: 'app' | 'help' | 'clock' | 'alert';
  app?: AppInfo;
  badge?: AppLogoBadgeKind;
  title: string;
  sub: string;
};

function Circle({ icon }: { icon: typeof faQuestion }) {
  return (
    <span
      aria-hidden="true"
      className="inline-flex size-12 items-center justify-center rounded-full bg-muted text-[22px] text-muted-foreground"
    >
      <FontAwesomeIcon icon={icon} />
    </span>
  );
}

/**
 * EmbeddedStatusBlock — A 48px visual and exactly two lines, so the block has the same
 * height on every status screen (waiting, done, didn't finish, help answers).
 */
export default function EmbeddedStatusBlock({
  visual,
  app,
  badge,
  title,
  sub,
}: Props) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-col items-center gap-3 text-center"
    >
      {visual === 'help' && <Circle icon={faQuestion} />}
      {visual === 'clock' && <Circle icon={faClock} />}
      {visual === 'alert' && <Circle icon={faExclamation} />}
      {visual === 'app' && (
        <AppLogo
          src={app?.logo}
          name={app?.name ?? ''}
          className="size-12 rounded-xl"
          badge={badge && <AppLogoBadge kind={badge} />}
        />
      )}
      <span className="flex min-w-0 flex-col items-center">
        <span className="text-[15px] leading-[22px] font-semibold">
          {title}
        </span>
        <span className="max-w-[280px] text-[13px] leading-[18px] text-muted-foreground">
          {sub}
        </span>
      </span>
    </div>
  );
}
